import { useEffect, useState } from "react";
import { Calculator, Download, Minus, Plus, Share2 } from "lucide-react";
import { toast } from "sonner";
import { brl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const toNum = (v) => parseFloat(String(v).replace(",", ".")) || 0;

export const CostCalculator = ({ materials = [], title = "" }) => {
  const [prices, setPrices] = useState([]);
  const [copies, setCopies] = useState(1);

  useEffect(() => {
    setPrices(materials.map((m) => (m.price ? String(m.price).replace(".", ",") : "")));
  }, [materials]);

  if (!materials.length) return null;
  const subtotal = prices.reduce((s, p) => s + toNum(p), 0);

  const buildList = () => [
    `Lista de compras - ${title}`,
    `Peças: ${copies}`,
    "",
    ...materials.map((m, i) => `- ${m.name}${m.quantity ? ` (${m.quantity}${copies > 1 ? ` x${copies}` : ""})` : ""}: ${brl(toNum(prices[i]) * copies)}`),
    "",
    `Total estimado: ${brl(subtotal * copies)}`,
    window.location.href,
  ].join("\n");

  const download = () => {
    const url = URL.createObjectURL(new Blob([buildList()], { type: "text/plain;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `lista-de-compras-${title.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-") || "projeto"}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const share = async () => {
    const text = buildList();
    if (navigator.share) {
      try { await navigator.share({ title: `Lista de compras - ${title}`, text }); return; } catch (e) { if (e.name === "AbortError") return; }
    }
    try { await navigator.clipboard.writeText(text); toast.success("Lista copiada!"); } catch { toast.error("Não foi possível copiar a lista"); }
  };

  return (
    <div data-testid="cost-calculator" className="rounded-2xl border border-border bg-card p-5">
      <h3 className="flex items-center gap-2 font-bold"><Calculator className="h-5 w-5 text-primary" /> Calculadora de custo</h3>
      <p className="mt-1 text-xs text-muted-foreground">Ajuste os preços dos materiais na sua região.</p>
      <ul className="mt-4 space-y-2.5">
        {materials.map((m, i) => (
          <li key={i} className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-sm">{m.name}{m.quantity && <span className="ml-1 text-xs text-muted-foreground">({m.quantity})</span>}</span>
            <div className="relative w-28 shrink-0">
              <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">R$</span>
              <Input
                data-testid={`calc-price-${i}`}
                inputMode="decimal"
                value={prices[i] ?? ""}
                onChange={(e) => setPrices((arr) => arr.map((x, idx) => (idx === i ? e.target.value.replace(/[^\d.,]/g, "") : x)))}
                placeholder="0,00"
                className="h-8 rounded-lg pl-8 text-right text-sm font-mono"
              />
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
        <span className="text-sm">Quantas peças?</span>
        <div className="flex items-center gap-2">
          <Button type="button" size="icon" variant="outline" data-testid="calc-copies-minus" className="h-7 w-7 rounded-full" onClick={() => setCopies((c) => Math.max(1, c - 1))}><Minus className="h-3.5 w-3.5" /></Button>
          <span data-testid="calc-copies" className="w-6 text-center font-mono text-sm">{copies}</span>
          <Button type="button" size="icon" variant="outline" data-testid="calc-copies-plus" className="h-7 w-7 rounded-full" onClick={() => setCopies((c) => c + 1)}><Plus className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="mt-4 flex items-baseline justify-between rounded-xl bg-secondary px-4 py-3">
        <span className="text-sm font-medium">Total estimado</span>
        <span data-testid="calc-total" className="text-xl font-extrabold text-primary">{brl(subtotal * copies)}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" size="sm" data-testid="calc-download-btn" onClick={download} className="rounded-full gap-1.5"><Download className="h-4 w-4" /> Baixar lista</Button>
        <Button type="button" size="sm" data-testid="calc-share-btn" onClick={share} className="rounded-full gap-1.5"><Share2 className="h-4 w-4" /> Compartilhar</Button>
      </div>
    </div>
  );
};
